/**
 * API utility functions with automatic activity tracking and session management
 */

import { auth, performGlobalLogout } from '@/config/firebase';
import { getAnalyticsSessionId } from '@/services/analyticsService';

// Centralized API base URL helper.
//
// The resolution itself lives in the side-effect-free `./apiBase` module so
// URL builders and presentational primitives can use it without importing this
// file's Firebase/analytics side effects (GCS URL exposure Phase 4, DL-34).
// Re-exported here so existing importers are unaffected.
import { getApiBaseUrl } from './apiBase';
export { getApiBaseUrl };

// Key for storing last activity timestamp in localStorage
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

// Token cache with expiry tracking.
//
// round-13 #9 (§4.3 identity safety): the cache is keyed by the Firebase `uid` it was
// fetched for, and guarded by a monotonic `tokenCacheGeneration`. Both are captured
// BEFORE the async token fetch so a fetch that was IN FLIGHT across an identity switch
// (account A → "use a different email" → account B) can DROP its own stale write on
// resolve — otherwise A's late-resolving fetch would repopulate the cache and B's next
// request would read A's bearer, yielding a cross-identity 403. Reads only serve a
// cached token whose `uid` matches the CURRENT `auth.currentUser` — so even a race that
// slips a stale write past the generation check can never hand out another user's token.
interface TokenCache {
  token: string;
  expiryTime: number;
  uid: string;
}

let tokenCache: TokenCache | null = null;
// Bumped by clearTokenCache(); a token fetch that started before the bump discards its
// result on resolve (its captured generation is now stale).
let tokenCacheGeneration = 0;

// round-18 CX#10 (§4.3 identity safety): a monotonic AUTH-SESSION epoch, distinct from
// `tokenCacheGeneration`. It is bumped ONLY on a real Firebase sign-in/sign-out/switch
// transition (via {@link bumpAuthSessionEpoch}, called from AuthManager's onAuthStateChanged
// on any uid change), NEVER on a cache clear. This is the signal `generation` cannot be: the
// logout validator must distinguish "still the ORIGINAL session" from "a NEW session for the
// same uid established after a stale 401" — but it cannot use `generation`, because
// performGlobalLogout's OWN clearTokenCache bumps generation mid-logout and would self-abort
// every legitimate logout. The session epoch is immune to that cache-clear churn, so a stale
// A-request's logout aborts once a fresh A-session has bumped the epoch, while a genuine
// logout of the original session (epoch unchanged until sign-out) still proceeds.
let authSessionEpoch = 0;

/**
 * Metadata-only invalidation notice for long-lived, sensitive browser work.
 * Never include credentials here: subscribers only need the monotonic identity
 * coordinates in order to cancel work that began under an older generation.
 */
export interface AuthIdentityInvalidation {
  uid: string | null;
  generation: number;
  sessionEpoch: number;
}

type AuthIdentityInvalidationListener = (identity: AuthIdentityInvalidation) => void;
const authIdentityInvalidationListeners = new Set<AuthIdentityInvalidationListener>();

const notifyAuthIdentityInvalidated = (): void => {
  const identity: AuthIdentityInvalidation = {
    uid: auth.currentUser?.uid ?? null,
    generation: tokenCacheGeneration,
    sessionEpoch: authSessionEpoch,
  };
  for (const listener of authIdentityInvalidationListeners) listener(identity);
};

/** Subscribe to auth-generation changes. Returns an idempotent unsubscribe. */
export const subscribeAuthIdentityInvalidation = (
  listener: AuthIdentityInvalidationListener,
): (() => void) => {
  authIdentityInvalidationListeners.add(listener);
  return () => authIdentityInvalidationListeners.delete(listener);
};

/**
 * Bump the auth-session epoch. Call ONLY on a genuine Firebase identity transition
 * (sign-in, sign-out, or account switch) — NOT on a token refresh or a cache clear.
 * Wired from AuthManager.onAuthStateChanged when the current uid actually changes.
 */
export const bumpAuthSessionEpoch = (): void => {
  authSessionEpoch++;
  notifyAuthIdentityInvalidated();
};

/**
 * round-14 #4 (§4.3 identity safety) — an immutable snapshot of "which identity is this
 * request/refresh/retry/logout acting on". `uid` is the Firebase account; `generation` is
 * the monotonic clear-counter (bumped by clearTokenCache / clearCsrfTokenCache on every
 * logout or account switch). Together they identify one continuous authenticated session:
 * if EITHER changes between capture and use, the identity the work started as is no longer
 * the current identity, and the work must ABORT rather than act with/against the new one.
 *
 * round-13 #9 keyed the CACHES by uid+generation so a stale in-flight fetch could not
 * repopulate the cache under a new identity — but that only suppressed the WRITE. A caller
 * still received the stale credential (`return token`/`return csrf`) and could send it after
 * the switch, and a stale 401's retry/logout still acted on whatever identity happened to be
 * current. #4 closes DELIVERY too: a snapshot is captured once at the entry of each request
 * lifecycle (createAuthHeaders / fetchWithActivity) and threaded through the bearer fetch,
 * the CSRF fetch, the forced refresh, the retry header-rewrite, and the logout decision. Any
 * mismatch aborts — the credential is never delivered, the request is never replayed as
 * another user, and another user is never logged out for a request that was not theirs.
 * Round-17 #6 tightens this into one outer-boundary snapshot: request/apiCall captures it,
 * then passes that exact object through header construction and fetch delivery.
 */
export interface AuthIdentity {
  /**
   * The Firebase account uid, or `null` when the request was issued while signed OUT.
   * round-18 CX#9: a signed-out origin is a REAL, first-class snapshot (`uid: null`), not a
   * fence-disabling `null` snapshot — so a request that BEGAN anonymously and then sees a user
   * sign in mid-flight (`null → B`) fails the identity match and aborts, instead of silently
   * acquiring B's freshly-minted bearer. A request that stays anonymous (`uid` stays `null`)
   * still matches and proceeds.
   */
  uid: string | null;
  generation: number;
  /**
   * The auth-session epoch at capture (round-18 CX#10). Bumped only on real sign-in/out
   * transitions, so the logout validator can tell the original session from a fresh same-uid
   * one. Not used by request-delivery fences (those key on uid+generation).
   */
  sessionEpoch: number;
}

/**
 * Thrown when a request/refresh/retry that STARTED as one identity discovers, after an
 * await, that the current identity has changed (account switch or logout/clear). Callers
 * treat this as "abort this request" — it is NOT a normal auth failure to be retried; a
 * request that began as account A must never complete as, or log out, account B (round-14 #4).
 */
export class AuthIdentityChangedError extends Error {
  constructor(message = 'Auth identity changed while a request was in flight — aborting') {
    super(message);
    this.name = 'AuthIdentityChangedError';
  }
}

/**
 * Capture the CURRENT auth identity as an immutable snapshot. Read synchronously (no await) at
 * a request-lifecycle entry point so the snapshot reflects the identity the caller INTENDED to
 * act as. round-18 CX#9: this ALWAYS returns a snapshot — a signed-out origin is captured as
 * `{ uid: null, ... }` (never a bare `null`), so the identity fences remain ACTIVE for an
 * anonymous request and catch a `null → authenticated` switch mid-flight.
 */
export const captureAuthIdentity = (): AuthIdentity => {
  return {
    uid: auth.currentUser?.uid ?? null,
    generation: tokenCacheGeneration,
    sessionEpoch: authSessionEpoch,
  };
};

/**
 * True when `snapshot` still matches the LIVE identity: the same uid (or the same signed-OUT
 * origin) is current AND no clear/switch has bumped the generation since capture. A `null → B`
 * transition (anonymous origin, then B signs in) does NOT match — the safe (abort) direction;
 * an anonymous origin that stays anonymous (`null === null`) DOES match and proceeds. An
 * absent snapshot (legacy callers that pass nothing) returns false. Reads the live
 * `auth.currentUser` at call time so it sees a switch that happened during an in-flight await.
 */
export const identityStillMatches = (snapshot: AuthIdentity | null): boolean => {
  if (!snapshot) return false;
  // Sample both live components in one synchronous tick. An `await import()` between these
  // reads let a generation bump interleave after the generation check but before the uid read.
  const generation = tokenCacheGeneration;
  const currentUid = auth.currentUser?.uid ?? null;
  return generation === snapshot.generation && currentUid === snapshot.uid;
};

/** Fail closed before delivering credentials, requests, or response bodies across identities. */
export const assertAuthIdentity = (snapshot: AuthIdentity | null): void => {
  if (snapshot && !identityStillMatches(snapshot)) {
    throw new AuthIdentityChangedError();
  }
};

/**
 * Update the last activity timestamp in localStorage
 */
export const updateLastActivity = (): void => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch (error) {
    devError('Error updating last activity timestamp:', error);
  }
};

/**
 * Check if response indicates session expiration and attempt token refresh before logout
 *
 * round-14 #4 (§4.3 identity safety): `requestIdentity` is the snapshot of the identity that
 * ISSUED the request this 401 answers. A 401 for account A that arrives AFTER a switch to B
 * must neither refresh-and-retry as B (confused deputy) nor log B out for A's stale failure.
 * When the snapshot no longer matches the current identity, this returns `{ shouldRetry:
 * false }` WITHOUT refreshing or logging out — the orphaned A request simply surfaces its 401.
 */
const handleSessionExpiration = async (
  response: Response,
  attemptRefresh: boolean = true,
  requestIdentity?: AuthIdentity | null,
): Promise<{ shouldRetry: boolean }> => {
  if (response.status === 401 || response.status === 403) {
    let isTokenExpired = false;
    let isTokenInvalid = false;

    try {
      // Check Content-Type before attempting to parse
      const contentType = response.headers.get('content-type');
      const isJsonResponse = contentType?.includes('application/json');

      if (isJsonResponse) {
        // Only parse JSON if Content-Type indicates JSON
        const responseText = await response.text();
        const data = JSON.parse(responseText);

        // Check for token expiration (recoverable).
        //
        // round-14 #8 (§4.3 auth): match the backend's STRUCTURED code FIRST — the
        // owner-route auth middleware (my-backend/src/middleware/auth.ts) now emits
        // `code: 'TOKEN_EXPIRED'` on a genuine `auth/id-token-expired`, which is a
        // recoverable expiry the forced-refresh retry SHOULD fire for. The phrase
        // fallback is ALSO broadened from `'token expired'` to `'expired token'` so a
        // pre-redeploy backend whose only signal is the message `'Invalid or expired
        // token'` is still recognized (that phrase contains `'expired token'` but NOT
        // the contiguous `'token expired'`, which is exactly why the retry silently
        // never fired before this fix). `SESSION_EXPIRED` is the legacy session code.
        isTokenExpired =
          data.code === 'TOKEN_EXPIRED' ||
          data.code === 'SESSION_EXPIRED' ||
          (typeof data.error === 'string' &&
           (data.error.toLowerCase().includes('token expired') ||
            data.error.toLowerCase().includes('expired token')));

        // Check for invalid/revoked token (unrecoverable).
        //
        // round-14 #8: match the structured `TOKEN_INVALID` / `TOKEN_REVOKED` codes the
        // backend now emits, alongside the existing phrase fallbacks. NOTE the code
        // `'TOKEN_EXPIRED'` deliberately does NOT set this flag — an expiry is recoverable
        // and must fall to the forced-refresh path, not straight to logout.
        //
        // The ambiguous message-only `'Invalid or expired token'` (a backend that has not
        // yet redeployed the structured code) is deliberately NOT matched here: matching
        // its `'invalid'` would flip `isTokenInvalid` true and, via the
        // `isTokenExpired && !isTokenInvalid` guard, SUPPRESS the very refresh this fix
        // restores. Treating that message as recoverable-expiry is the correct
        // recovery-first posture — a forced refresh either produces a fresh valid token
        // (transparent retry) or returns null (genuinely dead session → logout). We only
        // require the contiguous `'invalid token'` phrase, which that message does NOT
        // contain, so message-only responses route to expiry-refresh while a truly-invalid
        // token (structured `TOKEN_INVALID`, or an explicit `'invalid token'`/`'revoked'`/
        // `'malformed'` message) still routes straight to logout.
        isTokenInvalid =
          data.code === 'TOKEN_INVALID' ||
          data.code === 'TOKEN_REVOKED' ||
          (typeof data.error === 'string' && (
            data.error.toLowerCase().includes('invalid token') ||
            data.error.toLowerCase().includes('revoked') ||
            data.error.toLowerCase().includes('malformed')
          ));
      } else {
        // For non-JSON responses (e.g., "Unauthorized" text), treat as expired token
        // Most backends return plain text for 401s, and these are usually recoverable
        devLog('Non-JSON auth error response detected, treating as expired token');
        isTokenExpired = true;
      }
    } catch (parseError) {
      // If we can't parse the response, assume it's an expired token
      // Better to attempt refresh than immediately logout the user
      devLog('Could not parse auth error response, assuming expired token');
      isTokenExpired = true;
    }

    // round-14 #4: if this 401 belongs to a request that started as a DIFFERENT identity
    // than the one now signed in (the account switched while the request/response was in
    // flight), do NOT refresh, retry, or log out. Refreshing would mint B's token for A's
    // replayed request (confused deputy); logging out would kill B's session over A's stale
    // failure. Return no-retry so A's orphaned request simply surfaces its 401. Checked
    // BEFORE any refresh/logout side effect. The guard applies ONLY when there was a real
    // originating identity to protect — a `null` snapshot (request issued while signed out)
    // or `undefined` (legacy caller) leaves the existing refresh/logout behavior unchanged.
    if (requestIdentity && !identityStillMatches(requestIdentity)) {
      devLog('401 belongs to a prior identity (account switched in flight) — not retrying or logging out');
      return { shouldRetry: false };
    }

    // Attempt refresh if token appears expired and not definitively invalid
    if (isTokenExpired && !isTokenInvalid && attemptRefresh) {
      devLog('Token expired. Attempting refresh before logout...');

      // round-13 #12: `getAuthTokenWithActivity(true)` NEVER throws on a refresh
      // failure — its catch returns `null` (see below). So the previous try/catch was
      // dead and a genuinely FAILED forced refresh still fell into `return
      // { shouldRetry: true }`, and the retry path (fetchWithActivity ~212) then found
      // no fresh token and REPLAYED the stale Authorization header → a guaranteed
      // second 401. Signal a retry ONLY when the refresh actually produced a token;
      // a null (failed) refresh falls through to the unrecoverable-session logout.
      //
      // round-14 #4: bind the forced refresh to the request identity — if the account
      // switches DURING the refresh, `getAuthTokenWithActivity` throws
      // `AuthIdentityChangedError`; we treat that as "no usable refresh for THIS request"
      // (no retry, no logout of the new identity) and surface the original 401.
      let refreshed: string | null;
      try {
        refreshed = await getAuthTokenWithActivity(true, requestIdentity); // Force token refresh
      } catch (refreshErr) {
        if (refreshErr instanceof AuthIdentityChangedError) {
          devLog('Identity changed during forced refresh — not retrying or logging out');
          return { shouldRetry: false };
        }
        throw refreshErr;
      }
      if (refreshed) {
        devLog('Token refresh successful - retry request');
        return { shouldRetry: true }; // Signal caller to retry
      }
      devError('Token refresh failed (no token) — falling through to logout');
      // Fall through to logout below.
    }

    // If refresh failed or token is definitively invalid, logout.
    //
    // round-14 #4: re-check identity immediately before the logout side effect — the account
    // could have switched during the refresh attempt above. Never log out an identity that
    // did not issue this request. A `null`/`undefined` requestIdentity (legacy / signed-out
    // issuer) skips the re-check and logs out as before.
    if (isTokenExpired || isTokenInvalid) {
      if (requestIdentity && !identityStillMatches(requestIdentity)) {
        devLog('Session unrecoverable, but identity changed in flight — not logging out the new identity');
        return { shouldRetry: false };
      }
      devLog('Session unrecoverable. Triggering logout...');
      // round-15 #8 + round-18 CX#8/CX#10 (§4.3 identity safety): the check above is a single
      // point-in-time gate, but `performGlobalLogout` runs destructive phases (cache clear +
      // cross-tab broadcast + storage/cookie/Firebase teardown) across a 50ms delay + dynamic
      // imports — the account can switch, or a FRESH same-uid session can be established, in that
      // window. Thread the originating identity in as a SYNCHRONOUS re-validatable predicate so
      // the logout aborts BEFORE each side effect if it no longer matches.
      //
      // The predicate checks uid AND `sessionEpoch` (NOT `generation`): generation cannot be
      // used because performGlobalLogout's own clearTokenCache bumps it mid-logout and would
      // self-abort every legitimate logout. `sessionEpoch` is immune to cache-clear churn — it
      // only advances on a real sign-in/out — so a stale A-request's logout correctly aborts
      // once A has re-authenticated into a NEW session (epoch advanced), while a genuine logout
      // of the original session (epoch unchanged until sign-out) still proceeds. An absent
      // `requestIdentity` (legacy) logs out unconditionally.
      await performGlobalLogout(
        requestIdentity
          ? {
              validateIdentity: () =>
                (auth.currentUser?.uid ?? null) === requestIdentity.uid &&
                authSessionEpoch === requestIdentity.sessionEpoch,
            }
          : undefined,
      );
      return { shouldRetry: false };
    }
  }

  return { shouldRetry: false };
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Extended request init interface with custom options
interface ExtendedRequestInit extends RequestInit {
  maxRetries?: number;
  retryDelay?: number;
  isUserInitiated?: boolean;
  /**
   * Opt IN to network-error retry for a NON-idempotent method (round-8d #16).
   * By default, `fetchWithActivity` never retries a POST/PUT/PATCH/DELETE after a
   * network error, because the request may already have been committed server-side
   * and only the response was lost — replaying it would re-run a non-idempotent
   * operation (e.g. burn a single-use Connect/claim/verify token that the first
   * attempt already consumed, yielding a spurious 400 "invalid token"). Set this to
   * `true` ONLY when the endpoint is provably idempotent (same request → same effect),
   * e.g. an idempotency-keyed write. Idempotent methods (GET/HEAD/OPTIONS) always
   * retry regardless of this flag.
   */
  idempotent?: boolean;
}

/**
 * HTTP methods that are safe to replay after a lost response: replaying produces the
 * same server state, so a network-error retry can never double-commit. (RFC 9110
 * §9.2.2 idempotent methods; OPTIONS is metadata-only.)
 */
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Decide whether a request may be retried after a NETWORK error (not a 429 — a 429 is
 * rejected by the rate limiter BEFORE the handler runs, so it never commits and is
 * always safe to retry). Retry is allowed only when the method is inherently
 * idempotent, or the caller has explicitly asserted idempotency via `idempotent:true`.
 * A method-less request defaults to GET (fetch's default) and is therefore retryable.
 */
const isNetworkRetryAllowed = (init?: ExtendedRequestInit): boolean => {
  const method = (init?.method ?? 'GET').toUpperCase();
  if (IDEMPOTENT_METHODS.has(method)) return true;
  return init?.idempotent === true;
};

/**
 * Enhanced fetch function that automatically updates activity timestamp, handles session expiration,
 * and includes retry logic for rate limiting
 * Use this instead of regular fetch for API calls to maintain session activity
 */
export const fetchWithActivity = async (
  input: RequestInfo | URL,
  init?: ExtendedRequestInit,
  requestIdentity: AuthIdentity | null = captureAuthIdentity(),
): Promise<Response> => {
  // Update activity timestamp before making the API call
  // Only treat as activity if this is a user-initiated request (default true; caller may override)
  const isUserInitiated = init?.isUserInitiated !== false;
  if (isUserInitiated) {
    updateLastActivity();
  }

  // The outer request boundary owns this snapshot and threads the SAME value through header
  // construction and delivery. The default preserves identity fencing for direct/legacy callers.

  // Extract retry options
  const maxRetries = init?.maxRetries ?? 3;
  const baseRetryDelay = init?.retryDelay ?? 1000; // 1 second base delay
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // One common pre-send fence covers the initial request and every replay. In particular,
      // credentials built for A cannot be sent if the account changed to B after header creation.
      assertAuthIdentity(requestIdentity);
      const response = await fetch(input, init);

      // Handle rate limiting with exponential backoff
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter
          ? parseInt(retryAfter) * 1000
          : baseRetryDelay * Math.pow(2, attempt); // Exponential backoff

        if (attempt < maxRetries) {
          devLog(`Rate limited. Retrying after ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
          await sleep(delay);
          // round-15 #6 (§4.3 identity safety): re-check the ORIGINATING identity AFTER the
          // backoff sleep and BEFORE replaying. The 429 came back for identity A; if the account
          // switched to B during the (potentially seconds-long) `Retry-After` delay, replaying
          // `input`/`init` would re-send A's request with A's still-attached bearer under B (or,
          // once the header is rebuilt elsewhere, as B) — a confused-deputy replay. ABORT the
          // orphaned A request instead (throw, consistent with the rest of the #4 threading —
          // callers treat `AuthIdentityChangedError` as "this request is cancelled", not a
          // retriable failure). A `null` requestIdentity (issued signed-out) has nothing to
          // protect and no-ops, so a legitimate anonymous retry is unaffected.
          if (requestIdentity && !identityStillMatches(requestIdentity)) {
            devLog('Identity changed during 429 backoff — aborting retry (not replaying A as B)');
            throw new AuthIdentityChangedError();
          }
          continue; // Retry the request
        }
      }

      // Check for session expiration before processing the response
      if (!response.ok && response.status !== 429) {
        // Clone the response so we can read it for session expiration check
        const responseClone = response.clone();

        // Only attempt refresh on first attempt to prevent multiple refresh tries.
        // round-14 #4: bind the whole expiry-handling (refresh + logout decision) to the
        // request's originating identity so a stale cross-identity 401 neither refreshes-and-
        // retries as, nor logs out, the newly-switched-in account.
        const { shouldRetry } = await handleSessionExpiration(
          responseClone,
          attempt === 0,
          requestIdentity,
        );

        if (shouldRetry && attempt === 0) {
          // round-14 #4 + round-15 #6: even though `handleSessionExpiration` already gated
          // identity, re-check here immediately before rewriting the Authorization header and
          // replaying — the account could switch in the microtask gap. If it changed, ABORT:
          // do NOT re-issue A's URL/body with B's freshly-minted bearer (the confused-deputy
          // replay). round-15 #6 tightens the prior "surface the original 401" to a clean
          // ABORT (throw) — an orphaned A 401 must not be DELIVERED into the switched-in B's
          // session either; it is cancelled, consistent with the post-response delivery guard
          // below and the token/CSRF abort semantics.
          if (requestIdentity && !identityStillMatches(requestIdentity)) {
            devLog('Identity changed before retry replay — aborting request (not replaying/delivering A as B)');
            throw new AuthIdentityChangedError();
          }
          devLog('Token refreshed. Retrying request with new token...');

          // FIX: Update Authorization header with freshly cached token. Bind the fetch to the
          // request identity so a switch mid-fetch throws rather than handing back B's token.
          //
          // round-15 #6: any error from this token read PROPAGATES (no local catch). In
          // particular an `AuthIdentityChangedError` — thrown when the identity switched — is a
          // clean ABORT: the request is cancelled, never replayed as B and never surfaced as A's
          // original 401 into the switched-in B. (Previously this was caught and turned back into
          // `return response`, surfacing A's 401; #6 removes that in favour of the abort,
          // consistent with the post-response delivery guard below.)
          const freshToken: string | null = await getAuthTokenWithActivity(false, requestIdentity); // cached (already refreshed)
          if (freshToken && init?.headers) {
            // Update the Authorization header with the new token
            if (init.headers instanceof Headers) {
              init.headers.set('Authorization', `Bearer ${freshToken}`);
            } else if (Array.isArray(init.headers)) {
              // Handle array of tuples format
              const authIndex = init.headers.findIndex(([key]) => key.toLowerCase() === 'authorization');
              if (authIndex >= 0) {
                init.headers[authIndex] = ['Authorization', `Bearer ${freshToken}`];
              } else {
                init.headers.push(['Authorization', `Bearer ${freshToken}`]);
              }
            } else {
              // Handle object format
              (init.headers as Record<string, string>)['Authorization'] = `Bearer ${freshToken}`;
            }
            devLog('Updated Authorization header with fresh token for retry');
          }

          // Use existing loop's retry mechanism - continue to next iteration
          continue;
        }
      }

      // round-15 #6 (§4.3 identity safety): validate the ORIGINATING identity AFTER the response
      // resolves and BEFORE delivering it. The fetch started as identity A; if the account
      // switched to B while it was in flight, this A response must NOT be delivered — it would
      // populate B's UI/query state with A's data (a cross-identity data leak / confused
      // deputy), and any orphaned A 401 must abort rather than surface into B's session. ABORT
      // (throw) so the caller cancels this request instead of consuming the wrong identity's
      // response. Placed on the common delivery path so it covers ok AND non-ok responses (a
      // 200 payload, a 401/403/5xx). A `null` requestIdentity (signed-out issuer) has nothing
      // to protect and no-ops — a legitimate anonymous request still returns normally. Same
      // identity → `identityStillMatches` is true → no throw, so every legitimate same-identity
      // response (including a genuine logout's 401) is delivered unchanged.
      if (requestIdentity && !identityStillMatches(requestIdentity)) {
        devLog('Identity changed while a request was in flight — not delivering A\'s response to B');
        throw new AuthIdentityChangedError();
      }

      // Update activity timestamp after successful API call (only if user initiated)
      if (response.ok && isUserInitiated) {
        updateLastActivity();
      }

      return response;
    } catch (error) {
      lastError = error as Error;

      // round-15 #6: an identity-change abort is a deliberate cancellation, NOT a network
      // failure to be retried — propagate it immediately (never fold it into the network-retry
      // path below, which would replay A's request as B). This mirrors how the token/CSRF
      // fetches let `AuthIdentityChangedError` escape rather than swallowing it.
      if (error instanceof AuthIdentityChangedError) {
        throw error;
      }

      // round-8d #16 (§4.3 auth-adjacent): a network error here means the request
      // was SENT but no response came back — the server may already have committed
      // the operation. Retrying a NON-idempotent method (POST/PUT/PATCH/DELETE) would
      // replay a committed write: for a single-use-token endpoint (connect
      // respond/decide, claim accept-invite, verify) the replay burns the
      // already-consumed token and surfaces a spurious 400 "invalid token" even though
      // the first attempt succeeded. So we retry ONLY idempotent requests (GET/HEAD/
      // OPTIONS, or a caller that explicitly asserted `idempotent:true`); otherwise we
      // stop and throw the network error so the caller can surface an "outcome
      // unknown — check status" state instead of a silent, destructive replay.
      if (attempt < maxRetries && isNetworkRetryAllowed(init)) {
        const delay = baseRetryDelay * Math.pow(2, attempt);
        devLog(`Request failed. Retrying after ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
        await sleep(delay);
        // round-15 #6 (§4.3 identity safety): re-check the ORIGINATING identity AFTER the
        // backoff sleep and BEFORE replaying a network-failed request. If the account switched
        // to B during the delay, replaying would re-send A's request (with A's attached bearer)
        // under B — the same confused-deputy replay the 429 path guards. ABORT the orphaned A
        // request instead of replaying it as B. `null` requestIdentity no-ops (anonymous retry
        // unaffected).
        if (requestIdentity && !identityStillMatches(requestIdentity)) {
          devLog('Identity changed during network-retry backoff — aborting retry (not replaying A as B)');
          throw new AuthIdentityChangedError();
        }
        continue;
      }

      // Non-idempotent request (or retries exhausted): do not replay — surface the
      // network error to the caller immediately.
      break;
    }
  }

  // If we've exhausted all retries, throw the last error
  throw lastError || new Error('Request failed after maximum retries');
};

/**
 * Get auth token and automatically update activity
 * FIX: Implements proper token caching to avoid forced refresh on every API call
 *
 * round-14 #4 (§4.3 identity safety): when `expectedIdentity` is supplied (by a request
 * lifecycle that captured a snapshot at its entry), a token whose fetch resolves AFTER the
 * identity changed is NOT delivered — the function throws {@link AuthIdentityChangedError}
 * instead of returning a bearer for the wrong account. Un-snapshotted callers keep the prior
 * round-13 #9 behavior (write-drop but still return the token to the fetching caller), which
 * is safe because the cache read is uid-keyed. The forced-refresh path inside
 * `handleSessionExpiration` passes no snapshot (it has already gated identity itself).
 */
export const getAuthTokenWithActivity = async (
  forceRefresh: boolean = false,
  expectedIdentity?: AuthIdentity | null,
): Promise<string | null> => {
  try {
    const user = (await import('@/config/firebase')).auth.currentUser;
    if (!user) return null;
    // round-14 #4: if the caller bound this fetch to a specific identity, that identity must
    // still be the current one AT ENTRY — a snapshot that no longer matches means the account
    // switched before we even started, so abort rather than fetch a token for the wrong user.
    if (expectedIdentity && !identityStillMatches(expectedIdentity)) {
      throw new AuthIdentityChangedError();
    }
    // Capture the identity + cache generation BEFORE the async token fetch (round-13 #9).
    // If either changes while `getIdTokenResult` is in flight (a logout/clear bumps the
    // generation; an account switch changes `currentUser`), this fetch's result is stale
    // and must not be cached under the new identity.
    const uid = user.uid;
    const generationAtStart = tokenCacheGeneration;

    const now = Date.now();
    const REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry

    // Check if we have a valid cached token — only serve it if it was fetched for the
    // CURRENT identity (round-13 #9: never hand account B a token cached for account A).
    if (
      !forceRefresh &&
      tokenCache &&
      tokenCache.uid === uid &&
      tokenCache.expiryTime > now + REFRESH_BUFFER
    ) {
      updateLastActivity();
      return tokenCache.token;
    }

    // Token is expired, near expiry, or forced refresh requested
    // Get both token and metadata including exact expiry time
    const idTokenResult = await user.getIdTokenResult(forceRefresh);
    const token = idTokenResult.token;

    // Use the exact expiry time from Firebase instead of approximation
    // expirationTime is an ISO string, convert to epoch milliseconds
    const expiryTime = new Date(idTokenResult.expirationTime).getTime();

    // round-13 #9: drop this write if the identity switched or the cache was cleared
    // while the fetch was in flight — otherwise a late-resolving account-A fetch would
    // repopulate the cache after an account-B switch, leaking A's bearer to B. We still
    // RETURN the freshly fetched token to THIS caller (it is valid for the identity that
    // requested it); we just refuse to persist it under a now-stale identity/generation.
    const identityChanged =
      tokenCacheGeneration !== generationAtStart ||
      (await import('@/config/firebase')).auth.currentUser?.uid !== uid;
    if (identityChanged) {
      // round-14 #4: for a SNAPSHOT-BOUND caller (a request lifecycle), delivering this
      // stale token would let a request that started as A be SENT with A's bearer after the
      // switch to B (or worse, be replayed as B). ABORT — throw so createAuthHeaders /
      // fetchWithActivity cancel the request instead of building it with a cross-identity
      // credential. For an un-snapshotted caller we preserve round-13 #9: drop the WRITE but
      // still return the token to THIS caller (it is valid for the identity that asked, and
      // the uid-keyed cache read can never hand it to another user).
      if (expectedIdentity) {
        devLog('Auth token fetch resolved after an identity switch — aborting (snapshot-bound)');
        throw new AuthIdentityChangedError();
      }
      devLog('Auth token fetch resolved after an identity switch/clear — not caching');
      return token;
    }
    tokenCache = { token, expiryTime, uid }; // Store RAW expiry (no buffer)

    // Sync token expiration to AuthManager AFTER cache update
    // This happens EVERY time we fetch a new token (both forced and natural refreshes)
    try {
      const { AuthManager } = await import('@/contexts/AuthManager');
      const manager = AuthManager.getInstance();
      manager.updateTokenExpiration(expiryTime); // Sync raw expiry time
      devLog('Synced token expiration to AuthManager:', new Date(expiryTime).toISOString());
    } catch (err) {
      // If AuthManager not available (early init), continue
      devError('Could not sync token expiration to AuthManager:', err);
    }

    updateLastActivity();
    return token;
  } catch (error) {
    // round-14 #4: an identity-change abort is NOT a token-fetch failure — it must
    // PROPAGATE so the request lifecycle cancels, not be swallowed into `null` (which the
    // caller would treat as "no auth header" and send an unauthenticated request). Re-throw
    // it before the generic null-on-error fallback. NOTE we do NOT null the cache here: the
    // abort is about THIS request's identity, and the cache belongs to whatever identity is
    // now current (cleared already by the switch's clearTokenCache).
    if (error instanceof AuthIdentityChangedError) {
      throw error;
    }
    devError('Error getting auth token:', error);
    // Clear cache on error
    tokenCache = null;
    return null;
  }
};

/**
 * Clear the token cache (call on logout or identity switch).
 *
 * round-13 #9: also bumps `tokenCacheGeneration` so any token fetch that was IN FLIGHT
 * when this ran discards its result on resolve instead of repopulating the cache under
 * a stale identity. This is what makes the synchronous clear in `InviteClaim`'s
 * account-switch race-safe rather than merely best-effort.
 */
export const clearTokenCache = (): void => {
  tokenCache = null;
  tokenCacheGeneration++;
  notifyAuthIdentityInvalidated();
};

/**
 * Generate a cryptographically secure random hex string
 * Uses crypto.getRandomValues() for security
 */
const generateSecureRandomHex = (bytes: number): string => {
  const array = new Uint8Array(bytes);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Generate a correlation ID for request tracing
 * Uses crypto.randomUUID() if available, otherwise uses crypto.getRandomValues()
 * Format: UUID (preferred) or secure hex string (24 chars)
 *
 * SECURITY: Never uses Math.random() - all fallbacks use cryptographic RNG
 */
export const generateCorrelationId = (): string => {
  // Primary: Use native UUID if available (most modern browsers/Node 19+)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback: Generate UUID v4 format using crypto.getRandomValues()
  // This is cryptographically secure unlike Math.random()
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    // Generate 16 random bytes for UUID v4
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant (10xx) bits per RFC 4122
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10xx

    // Format as UUID string
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }

  // Last resort fallback for extremely old environments
  // Still avoid Math.random() - use timestamp + high-resolution time if available
  const timestamp = Date.now().toString(36).padStart(9, '0');
  const hrTime = typeof performance !== 'undefined'
    ? Math.floor(performance.now() * 1000).toString(36).padStart(8, '0')
    : '00000000';
  // This is less secure but better than Math.random() - at least it's unique per millisecond
  return `${timestamp}-${hrTime}-fallback`;
};

// Phase 6.5 — CSRF token memo. Token is session-scoped on the backend and
// rotates on logout (see `services/csrfProtection.ts::invalidateUserTokens`).
// We fetch on demand, cache in-memory for the session, and clear on logout.
//
// round-13 #9 (§4.3 identity safety): the CSRF memo carries the same cross-identity
// hazard as the bearer cache. It is keyed by the Firebase `uid` it was fetched for and
// guarded by a monotonic `csrfCacheGeneration` (captured before the async fetch), so a
// CSRF fetch that was in flight across an account switch drops its stale write, and a
// read only serves a CSRF token whose `uid` matches the current identity. Without this,
// account A's in-flight CSRF fetch could repopulate the memo after a switch to B, and
// B's mutating request would carry A's CSRF token → backend 403.
let csrfTokenCache: { token: string; fetchedAt: number; uid: string } | null = null;
let csrfCacheGeneration = 0;
const CSRF_TTL_MS = 30 * 60 * 1000;

async function getCsrfToken(expectedIdentity?: AuthIdentity | null): Promise<string | null> {
  // Capture identity + generation BEFORE any await (round-13 #9).
  const uid = (await import('@/config/firebase')).auth.currentUser?.uid ?? null;
  if (!uid) return null;
  // round-14 #4: if bound to a request's identity, that identity must still be current at
  // entry — abort a CSRF fetch whose account already switched.
  if (expectedIdentity && !identityStillMatches(expectedIdentity)) {
    throw new AuthIdentityChangedError();
  }
  const generationAtStart = csrfCacheGeneration;

  const now = Date.now();
  // Serve the memo only if it was fetched for the CURRENT identity and is fresh.
  if (
    csrfTokenCache &&
    csrfTokenCache.uid === uid &&
    now - csrfTokenCache.fetchedAt < CSRF_TTL_MS
  ) {
    return csrfTokenCache.token;
  }
  try {
    // Thread the snapshot through the bearer fetch too — a CSRF request must present the
    // bearer of the SAME identity it will be memoized under (round-14 #4).
    const token = await getAuthTokenWithActivity(false, expectedIdentity);
    if (!token) return null;
    const res = await fetch(`${getApiBaseUrl()}/api/auth/csrf-token`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });

    // round-15 #5 (§4.3 identity safety): re-check identity IMMEDIATELY after the CSRF fetch
    // await, BEFORE branching on `res.ok`. The prior code only re-checked inside `if (csrf)`,
    // so the `!res.ok` (and empty-body) paths returned `null` with NO post-await guard. For a
    // snapshot-bound caller a `null` CSRF is NOT harmless: `createAuthHeaders` still assembled
    // the header set with the bearer it had ALREADY obtained for identity A, so a CSRF fetch
    // that was in flight across an A→B switch and returned non-OK let A's bearer ride out under
    // B (no CSRF header, but a cross-identity bearer). Binding the check here — covering the
    // non-OK, empty-body, AND ok paths uniformly — means a switched identity ABORTS the whole
    // header build (via `createAuthHeaders`) rather than delivering ANY credential-bearing
    // result for the wrong identity. The generation check is the same {@link identityStillMatches}
    // predicate the rest of the #4 threading uses; the uid re-read sees an in-flight switch.
    const identityChangedPostFetch =
      csrfCacheGeneration !== generationAtStart ||
      ((await import('@/config/firebase')).auth.currentUser?.uid ?? null) !== uid;
    if (identityChangedPostFetch && expectedIdentity) {
      // Snapshot-bound: the request that started as A must not complete/deliver as B — abort
      // the whole header build. (An un-snapshotted caller falls through to the round-13 #9
      // write-drop below, which is safe because the memo read is uid-keyed.)
      throw new AuthIdentityChangedError();
    }

    if (!res.ok) return null;
    const body = await res.json();
    const csrf = typeof body?.csrfToken === 'string' ? body.csrfToken : null;
    if (csrf) {
      // round-13 #9: drop this write if the identity switched or the memo was cleared
      // while the fetch was in flight — otherwise a late account-A fetch repopulates the
      // memo after an account-B switch, leaking A's CSRF token to B. (For a snapshot-bound
      // caller the identity-change case already threw above; this still guards the
      // un-snapshotted caller's WRITE and re-reads after the extra `res.json()` await.)
      const identityChanged =
        csrfCacheGeneration !== generationAtStart ||
        ((await import('@/config/firebase')).auth.currentUser?.uid ?? null) !== uid;
      if (identityChanged) {
        // round-14 #4: for a SNAPSHOT-BOUND caller, do NOT deliver this stale CSRF token —
        // it would ride on B's mutating request as A's CSRF (or A's request would be sent
        // with a CSRF for the wrong session). ABORT. For an un-snapshotted caller we keep
        // round-13 #9 (drop the write, still return — the memo read is uid-keyed).
        if (expectedIdentity) {
          throw new AuthIdentityChangedError();
        }
      } else {
        csrfTokenCache = { token: csrf, fetchedAt: now, uid };
      }
    }
    return csrf;
  } catch (err) {
    // round-14 #4: an identity-change abort must PROPAGATE, not be swallowed into `null`
    // (which createAuthHeaders would treat as "no CSRF header" and still send the request).
    if (err instanceof AuthIdentityChangedError) {
      throw err;
    }
    return null;
  }
}

export function clearCsrfTokenCache(): void {
  csrfTokenCache = null;
  csrfCacheGeneration++;
}

/**
 * Create authorization headers with Bearer token and update activity
 * FIX: No longer forces refresh in production - relies on smart caching in getAuthTokenWithActivity
 * Includes X-Request-ID header for request tracing/correlation
 * Phase 6.5: Attaches X-CSRF-Token on mutating requests. Callers pass the
 * HTTP method via `method` so we only fetch/attach the CSRF header when
 * necessary.
 */
export const createAuthHeaders = async (
  additionalHeaders: Record<string, string> = {},
  method?: string,
  requestIdentity: AuthIdentity | null = captureAuthIdentity(),
): Promise<Record<string, string>> => {
  // round-14 #4 + round-17 #6 (§4.3 identity safety): bind BOTH the bearer fetch and the CSRF
  // fetch to the one snapshot supplied by the outer request boundary (or captured by the
  // default for a direct caller). This is the load-bearing sequence from the
  // finding — A begins header creation, its bearer/CSRF fetch is in flight, the caches are
  // cleared and the account switches to B, then A's fetch resolves. Without the snapshot the
  // pre-#4 code RETURNED A's stale credential and this function assembled A's bearer+CSRF
  // into a header set that was then sent AFTER the switch. With the snapshot, either fetch
  // throwing `AuthIdentityChangedError` aborts the WHOLE header build, so a header set is
  // never assembled from a credential that no longer belongs to the current identity. A
  // null snapshot (no user) threads through as "unauthenticated", unchanged from before.
  // Use cached token when valid, only refresh when necessary
  const token = await getAuthTokenWithActivity(false, requestIdentity);

  let sessionId = '';
  try { sessionId = getAnalyticsSessionId() || ''; } catch { /* safe fallback */ }

  const mutating = method && !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
  const csrf = mutating ? await getCsrfToken(requestIdentity) : null;

  return {
    'Content-Type': 'application/json',
    'X-Request-ID': generateCorrelationId(),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(sessionId ? { 'X-Analytics-Session-Id': sessionId } : {}),
    ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    ...additionalHeaders,
  };
};

/**
 * Wrapper for API calls with automatic auth headers, activity tracking, and session management
 */
export const apiCall = async (
  url: string,
  options: Omit<RequestInit, 'headers'> & { headers?: Record<string, string> } = {}
): Promise<Response> => {
  const requestIdentity = captureAuthIdentity();
  const { headers = {}, ...restOptions } = options;

  // Fix relative URLs by prefixing with backend domain
  // This ensures consistency with production architecture
  const finalUrl = url.startsWith('/api')
    ? `${getApiBaseUrl()}${url}`
    : url;

  const authHeaders = await createAuthHeaders(headers, restOptions.method, requestIdentity);

  return fetchWithActivity(finalUrl, {
    ...restOptions,
    headers: authHeaders,
  }, requestIdentity);
};

/**
 * Track successful login to backend for analytics
 * Called after Firebase client-side authentication succeeds
 * Updates user_metadata.lastLogin and logs LOGIN_SUCCESS event
 *
 * Fire-and-forget: Does not block the login flow
 */
export const trackLoginSuccess = async (): Promise<void> => {
  try {
    const response = await apiCall('/api/auth/track-login', {
      method: 'POST',
    });

    if (!response.ok) {
      devError('Failed to track login:', response.status);
    }
  } catch (error) {
    // Fire-and-forget - don't let tracking errors affect login
    devError('Error tracking login (non-blocking):', error);
  }
};
